import Big from 'big.js';

import calculateCarbonData from 'src/lib/carbondata/calculator';
import { EMISSION_FACTORS } from 'src/lib/carbondata/constants';
import { emissionFactorForRegion, emissionFactors } from 'src/lib/carbondata/helpers';
import {
  BUY, BUYER, DIRECTIONS, METER, SELLER, TRADE,
  TRADE_DIRECTIONS_LABEL, TRADE_TYPE_RESIDUAL,
  UNIT_CARBON_EMISSIONS_ACRONYM,
} from 'src/util/constants';
import { getCounterFactualValueFromOrderedResidualRules } from 'src/util/counterfactual';
import {
  extractData, filterRulesByTradePointId,
  getCounterfactualAvailability, getMemberNodes, getTimeStamp, getUntradedVolume,
  toBigEnergyValue,
} from 'src/util/mainDataBuilder';
import { measureSince, nowMs } from 'src/util/perfMarks';
import { dateRangeToTimeRange } from 'src/util/time';

/**
 * Calculate units for the JSON data download.
 * @param {Big} carbonEmissionality
 * @returns {number} - units.
 */
const toFixedDecimalUnits = (carbonEmissionality) => Big(
  carbonEmissionality,
).minus(Big(carbonEmissionality).mod(1)).toNumber();

/**
 * Calculate nanos for the JSON data download.
 * @param {Big} carbonEmissionality
 * @returns {number} - nanos.
 */
export const toFixedDecimalNanos = (carbonEmissionality) => Big(
  carbonEmissionality,
).mod(1).times(10 ** 9).toFixed(0);

/**
 * Calculate the value object for the JSON data download.
 * @param {Big} big - carbon emissionality
 * @returns {object} - {units, nanos}.
 */
const toFixedDecimal = (big) => ({
  units: toFixedDecimalUnits(big), nanos: toFixedDecimalNanos(big),
});

/**
 * Builds the carbon emissions data for the json (data pack) download.
 * @param {object} property
 * @param {object} dateRange - {start, finish}
 * @param {string} [timezone] - IANA zone the date range is expressed in. Defaults to the
 *   property's timezone.
 * @returns {object} - carbonEmissionalityData [{'AU-WA': [{start, finish, value, units}]}]
 */
export const buildCarbonEmissions = (property, dateRange, timezone = property?.timezone) => {
  if (!property) {
    return {};
  }

  const { publicHolidayRegion } = property;
  if (!timezone || !publicHolidayRegion) {
    return {};
  }

  const timerange = dateRangeToTimeRange(dateRange, timezone);
  const { start: startTime, finish: finishTime } = timerange;

  const validEmissionFactors = emissionFactors(
    EMISSION_FACTORS,
    publicHolidayRegion,
    startTime,
    finishTime,
  );

  const carbonData = {};

  validEmissionFactors.forEach((validEmissionFactor) => {
    const { timeRange: factorTimeRange } = validEmissionFactor;
    const { start: factorStart, finish: factorFinish } = factorTimeRange;
    const carbonEmissionalityForRegion = emissionFactorForRegion(
      validEmissionFactor,
      publicHolidayRegion,
    );
    const { region, value: carbonEmissionalityValue } = carbonEmissionalityForRegion;
    if (!carbonData[region]) {
      carbonData[region] = [];
    }

    carbonData[region].push({
      start: factorStart,
      finish: factorFinish,
      value: toFixedDecimal(carbonEmissionalityValue),
      carbonEmissionalityValue,
      units: UNIT_CARBON_EMISSIONS_ACRONYM,
    });
  });

  return carbonData;
};

/**
 * Index the first datum for each key, matching Array.filter(...)[0] semantics.
 * @param {Array<object>} data - Data to index.
 * @param {Function} keyFunc - Function returning an index key.
 * @returns {Map<*, object>} - First datum keyed by the selected value.
 */
const getFirstDatumIndex = (data, keyFunc) => {
  const index = new Map();
  data?.forEach((datum) => {
    const key = keyFunc(datum);
    if (typeof key === 'number' && Number.isNaN(key)) return;
    if (!index.has(key)) index.set(key, datum);
  });
  return index;
};

/**
 * Prepare timestamp indexes without modifying extracted or query data.
 * @param {Array<object>} extractList - Extracted meter data.
 * @returns {Array<object>} - Extracted data paired with direction indexes.
 */
const getPreparedExtractList = (extractList) => extractList.map((datum) => {
  const directionIndexes = {};
  DIRECTIONS.forEach((direction) => {
    const { meter, trades } = datum[direction] || {};
    directionIndexes[direction] = {
      hasMeterData: Boolean(meter?.data),
      meterDataByTimestamp: getFirstDatumIndex(meter?.data, (item) => item?.timestamp),
      trades: (trades || []).map((trade) => ({
        trade,
        hasTradeData: Boolean(trade?.data),
        tradeDataByTimestamp: getFirstDatumIndex(trade?.data, (item) => item?.range?.finish),
      })),
    };
  });
  return { datum, directionIndexes };
});

/**
 * Group residual rules by the actor trade point and order them by acceptedAt once.
 * Rules have already been limited to accepted and closed states by extractData.
 * @param {object} rules - Rules keyed by UUID.
 * @param {string} direction - Property direction (buy or sell).
 * @returns {object} - Ordered residual rules keyed by trade point.
 */
const getResidualRulesByTradePoint = (rules, direction) => {
  const rulesByTradePoint = {};
  const trader = direction === BUY ? BUYER : SELLER;
  Object.values(rules).forEach((rule) => {
    if (rule?.tradeType !== TRADE_TYPE_RESIDUAL) return;
    const tradePointId = rule[trader]?.tradePoint?.uuid;
    if (!tradePointId) return;
    if (!rulesByTradePoint[tradePointId]) rulesByTradePoint[tradePointId] = [];
    rulesByTradePoint[tradePointId].push(rule);
  });
  Object.values(rulesByTradePoint).forEach((residualRules) => {
    residualRules.sort((a, b) => a.acceptedAt - b.acceptedAt);
  });
  return rulesByTradePoint;
};

/**
 * Get a cached display timestamp for an aggregation and raw timestamp.
 * @param {Map<string, Map<number, object>>} timestampCache - Timestamp cache.
 * @param {string} aggregation - ISO8601 aggregation.
 * @param {number} timestamp - Raw timestamp.
 * @param {string} timezone - Property timezone.
 * @returns {object} - Luxon DateTime used by output and carbon calculations.
 */
const getCachedTimestamp = (timestampCache, aggregation, timestamp, timezone) => {
  if (!timestampCache.has(aggregation)) timestampCache.set(aggregation, new Map());
  const aggregationCache = timestampCache.get(aggregation);
  if (!aggregationCache.has(timestamp)) {
    aggregationCache.set(timestamp, getTimeStamp(aggregation, timestamp, timezone));
  }
  return aggregationCache.get(timestamp);
};

/**
 * Get the mainData entry for a direction and display timestamp, creating it when missing.
 * @param {object} mainData - Main data being built.
 * @param {string} direction - Property direction (buy or sell).
 * @param {number} finalTimestamp - Display timestamp in milliseconds.
 * @returns {object} - The entry with meterDataAggregates, tradeSetSummaries and
 *   untradedDataAggregates.
 */
const getDirectionDataEntry = (mainData, direction, finalTimestamp) => {
  const directionData = mainData[direction].data;
  if (!directionData[finalTimestamp]) {
    directionData[finalTimestamp] = {
      meterDataAggregates: {},
      tradeSetSummaries: {},
      untradedDataAggregates: {},
    };
  }
  return directionData[finalTimestamp];
};

/**
 * Build one trade set summary datum for a rule at a timestamp, with carbon and counterfactual.
 * @param {object} args - Datum inputs.
 * @param {string} args.direction - Property direction (buy or sell).
 * @param {string} args.meterId - Meter UUID.
 * @param {string} args.propertyRegion - Property public holiday region.
 * @param {Array<object>} args.residualRules - Residual rules for the trade point, ordered by
 *   acceptedAt.
 * @param {number} args.timestamp - Raw timestamp (interval finish).
 * @param {object} args.timestampDate - Luxon DateTime for the timestamp.
 * @param {Map<number, object>} args.tradeDataByTimestamp - Trade data indexed by range finish.
 * @param {string} args.tradePointId - Meter trade point ID.
 * @returns {object} - The trade datum, or a zero datum when there is no trade at the timestamp.
 */
const getTradeDatum = ({
  direction, meterId, propertyRegion, residualRules, timestamp, timestampDate,
  tradeDataByTimestamp, tradePointId,
}) => {
  const sourceDatum = tradeDataByTimestamp.get(timestamp);
  if (!sourceDatum) {
    return {
      value: 0, volume: 0, carbon: 0, counterfactual: NaN, meterId,
    };
  }
  const {
    directions, types, volume, ...sourceData
  } = sourceDatum;
  const tradeDirection = directions?.length === 1 ? directions[0] : '';
  const type = types?.length === 1 ? types[0] : '';
  const carbon = direction === BUY ? calculateCarbonData({
    dataType: TRADE,
    timestamp: timestampDate,
    energy: toBigEnergyValue(volume),
    propertyRegion,
    direction: tradeDirection,
    type,
  }) : 0;
  const counterfactual = type === TRADE_TYPE_RESIDUAL
    ? NaN : getCounterFactualValueFromOrderedResidualRules({
      direction: TRADE_DIRECTIONS_LABEL[direction],
      range: sourceDatum.range,
      tradePointId,
      volume,
    }, residualRules);
  return {
    ...sourceData,
    volume,
    direction: tradeDirection,
    type,
    meterId,
    tradePointId,
    carbon: Number(carbon),
    counterfactual,
  };
};

/**
 * Build one meter datum at a timestamp, with carbon for buy-side readings.
 * @param {object} args - Datum inputs.
 * @param {string} args.direction - Property direction (buy or sell).
 * @param {Map<number, object>} args.meterDataByTimestamp - Meter data indexed by timestamp.
 * @param {string} args.propertyRegion - Property public holiday region.
 * @param {number} args.timestamp - Raw timestamp.
 * @param {object} args.timestampDate - Luxon DateTime for the timestamp.
 * @returns {object} - The meter datum with carbon, or `{ carbon }` when there is no reading.
 */
const getMeterDatum = ({
  direction, meterDataByTimestamp, propertyRegion, timestamp, timestampDate,
}) => {
  const meterDatum = meterDataByTimestamp.get(timestamp) || {};
  const carbon = direction === BUY ? calculateCarbonData({
    dataType: METER,
    timestamp: timestampDate,
    energy: toBigEnergyValue(meterDatum.value),
    propertyRegion,
    direction: TRADE_DIRECTIONS_LABEL[direction],
  }) : 0;
  return { ...meterDatum, carbon: Number(carbon) };
};

/**
 * Add untraded volume, carbon and counterfactual per meter to every timestamp of a direction.
 * Modifies mainData in place.
 * @param {object} args - Inputs.
 * @param {object} args.mainData - Main data being built.
 * @param {string} args.direction - Property direction (buy or sell).
 * @param {string} args.propertyRegion - Property public holiday region.
 * @param {object} args.residualRulesByTradePoint - Ordered residual rules keyed by trade point.
 */
const addUntradedData = ({
  mainData, direction, propertyRegion, residualRulesByTradePoint,
}) => {
  const directionLabel = TRADE_DIRECTIONS_LABEL[direction];
  const directionData = mainData[direction].data;
  Object.entries(directionData).forEach(([timestamp, info]) => {
    const { meterDataAggregates, tradeSetSummaries } = info;
    const untradedDataByMeter = {};
    Object.entries(meterDataAggregates).forEach(([meterId, meter]) => {
      if (!meter) return;
      untradedDataByMeter[meterId] = { volume: toBigEnergyValue(meter.value) };
    });
    Object.values(tradeSetSummaries).forEach((tradeSummary) => {
      const { meterId, volume } = tradeSummary || {};
      const meterData = mainData.meters[meterId];
      // Cannot assume untraded data exists for all meters
      const untradedVolume = toBigEnergyValue(untradedDataByMeter[meterId]?.volume);
      if (meterData?.aggregation && meterDataAggregates[meterId] && untradedVolume.gt(0)) {
        untradedDataByMeter[meterId].volume = getUntradedVolume(
          untradedVolume,
          toBigEnergyValue(volume),
        );
      }
    });
    Object.entries(untradedDataByMeter).forEach(([meterId, untradedDatum]) => {
      const { tradePointId } = mainData.meters[meterId] || {};
      // Clamp unconditionally: the metered aggregate itself (not just the traded
      // subtraction above) can be a small negative due to floating-point noise.
      const volume = getUntradedVolume(untradedDatum.volume);
      const value = volume.eq(0) ? 0 : getCounterFactualValueFromOrderedResidualRules({
        direction: directionLabel,
        range: { start: timestamp, finish: timestamp },
        tradePointId,
        volume,
      }, residualRulesByTradePoint[tradePointId] || []);
      const carbon = calculateCarbonData({
        dataType: TRADE,
        timestamp,
        energy: volume,
        direction: directionLabel,
        type: TRADE_TYPE_RESIDUAL,
        propertyRegion,
      });
      untradedDataByMeter[meterId] = { volume, carbon, value };
    });
    directionData[timestamp].untradedDataAggregates = untradedDataByMeter;
  });
};

/**
 * Build main data that is to be used in property dashboard
 * @param {object} property
 * @param {object} timeSpan
 * @param {string} [timezone] - IANA zone the data was requested and aggregated in, which
 *   can differ from the property's own zone when the URL sets `tz`. Daily buckets are
 *   placed in this zone. Defaults to the property's timezone.
 * @returns {object} - main data
 */
export const buildMainData = (property, timeSpan, timezone = property.timezone) => {
  const buildStart = nowMs();
  const {
    address, externalIdentifier, meters: allMeters,
    publicHolidayRegion: propertyRegion,
    title, uuid,
  } = property;
  const meterNodes = getMemberNodes(allMeters) || [];
  const extractList = meterNodes.map((node) => extractData(node));

  const carbonEmissions = buildCarbonEmissions(property, timeSpan, timezone);
  const carbonEmissionality = {
    [propertyRegion]: carbonEmissions[propertyRegion],
  };

  const mainData = {
    property: {
      address,
      carbonEmissionality,
      externalIdentifier,
      region: propertyRegion,
      title,
      timezone,
      uuid,
    },
    buy: { rules: {}, data: {} },
    sell: { rules: {}, data: {} },
    meters: {},
  };
  const timestamps = { buy: [], sell: [] };

  // form rules and extract timestamp list
  extractList.forEach((data) => {
    const {
      rules, tradePointId, buy, sell, meters,
    } = data;
    buy.meter?.data?.forEach((datum) => timestamps.buy.push(datum?.timestamp));
    sell.meter?.data?.forEach((datum) => timestamps.sell.push(datum?.timestamp));
    Object.assign(mainData.buy.rules, filterRulesByTradePointId(rules, tradePointId, BUYER));
    Object.assign(mainData.sell.rules, filterRulesByTradePointId(rules, tradePointId, SELLER));
    mainData.meters[meters.uuid] = meters;
  });
  DIRECTIONS.forEach((direction) => {
    timestamps[direction] = [...new Set(timestamps[direction])];
  });

  const preparedExtractList = getPreparedExtractList(extractList);

  // looping over all the timestamps
  DIRECTIONS.forEach((direction) => {
    const residualRulesByTradePoint = getResidualRulesByTradePoint(
      mainData[direction].rules,
      direction,
    );
    const timestampCache = new Map();
    timestamps[direction].forEach((timestamp) => {
      preparedExtractList.forEach(({ datum, directionIndexes }) => {
        const { uuid: meterId, tradePointId } = datum?.meters || {};
        const { aggregation } = datum[direction]?.meter || {};
        if (!aggregation) return;
        const timestampDate = getCachedTimestamp(
          timestampCache,
          aggregation,
          timestamp,
          timezone,
        );
        const dataEntry = getDirectionDataEntry(mainData, direction, timestampDate.ts);
        const { hasMeterData, meterDataByTimestamp, trades } = directionIndexes[direction];

        // Add tradeset summary grouped by rule id
        trades.forEach(({ trade, hasTradeData, tradeDataByTimestamp }) => {
          const { ruleId } = trade?.key || {};
          if (!ruleId || !hasTradeData) return;
          dataEntry.tradeSetSummaries[ruleId] = getTradeDatum({
            direction,
            meterId,
            propertyRegion,
            residualRules: residualRulesByTradePoint[tradePointId] || [],
            timestamp,
            timestampDate,
            tradeDataByTimestamp,
            tradePointId,
          });
        });

        // add meter data grouped by meter trade point id
        if (hasMeterData && meterId) {
          dataEntry.meterDataAggregates[meterId] = getMeterDatum({
            direction,
            meterDataByTimestamp,
            propertyRegion,
            timestamp,
            timestampDate,
          });
        }
      });
    });
    addUntradedData({
      mainData, direction, propertyRegion, residualRulesByTradePoint,
    });
  });

  mainData.counterfactualAvailability = getCounterfactualAvailability(mainData);
  measureSince('property-show:buildMainData', buildStart);

  return mainData;
};
